import React, {useRef} from 'react';
import {Link, LinkProps, useParams} from 'react-router-dom';
import {Box, Tab, Tabs, TabProps, Typography} from '@material-ui/core';

import {useLocalization, useSuperUser} from '../../hooks';
import {FortniteGamesTab} from '../../routes/getRouteConfigs';
import {RedirectWithParams} from 'epic-diesel-common';
import Insights from './Insights';
import Performance from './Performance';
import Playrate from './Playrate';
import Rewards from './Rewards';
import GameXp from './GameXp';
import {withPageLayout, WithPageLayoutProps} from '../../hocs';
import {PDFExportProvider} from '../../components/v2/sections/fortnite-games/export/PDFExportProvider';
import TimeRangeLimitWarningProvider from '../../components/v2/sections/fortnite-games/components/time-range-limit/TimeRangeLimitWarningProvider';
import BulkCSVExportProvider from '../../components/v2/sections/fortnite-games/export/BulkCSVExportProvider';
import LegendsLimitWarningProvider from '../../components/v2/sections/fortnite-games/components/chart/legends/LegendsLimitWarningProvider';
import Alert from '../../components/alert/Alert';
export type FortniteGamesViewProps = WithPageLayoutProps;
import {FortnigtContainer} from './FortniteGames.styles';

const CODES = Object.values(FortniteGamesTab).reduce(
    (acc, tab: FortniteGamesTab) => ({
        ...acc,
        [tab]: `epic.affiliate.fortnite_games.${tab}`
    }),
    {}
);

const FortniteGamesView: React.FC<FortniteGamesViewProps> = props => {
    const fortniteGamesRef = useRef();
    const {tab} = useParams<{tab: FortniteGamesTab}>();
    const getMessage = useLocalization();
    const {accountId, isLoaded} = useSuperUser();

    const {
        enableFnGames,
        enableFnPerformanceTab,
        enableFnPlayrateTab,
        enableFnRewardsTab,
        enableSuperUser
    } = props.configState || {};

    const TABS = {
        [FortniteGamesTab.Insights]: {featureFlag: true, Component: Insights},
        [FortniteGamesTab.Performance]: {
            featureFlag: enableFnPerformanceTab,
            Component: Performance
        },
        [FortniteGamesTab.Playrate]: {featureFlag: enableFnPlayrateTab, Component: Playrate},
        [FortniteGamesTab.GameXp]: {featureFlag: true, Component: GameXp},
        [FortniteGamesTab.Rewards]: {featureFlag: enableFnRewardsTab, Component: Rewards}
    };

    const getFnTab = (isEnabled: boolean, tab: React.ReactElement): React.ReactElement =>
        isEnabled ? tab : <RedirectWithParams to="overview" />;

    if (!enableFnGames) {
        return <RedirectWithParams to="overview" />;
    }

    if (!Object.values(FortniteGamesTab).includes(tab)) {
        return <RedirectWithParams to="overview" />;
    }

    const LinkTab: React.ComponentType<TabProps & LinkProps> = Tab as React.ComponentType<
        TabProps & LinkProps
    >;

    return (
        <PDFExportProvider>
            <TimeRangeLimitWarningProvider>
                <BulkCSVExportProvider>
                    <LegendsLimitWarningProvider>
                        <FortnigtContainer ref={fortniteGamesRef}>
                            {enableSuperUser && accountId && (
                                <Alert
                                    type="INFO"
                                    severity="info"
                                    variant="filled"
                                    closeable
                                    message={`Viewing the page as account id ${accountId}`}></Alert>
                            )}
                            <Tabs value={tab} variant="scrollable">
                                {Object.values(FortniteGamesTab).map(
                                    tab =>
                                        TABS[tab].featureFlag && (
                                            <LinkTab
                                                key={tab}
                                                label={
                                                    <Typography variant="h5">
                                                        {getMessage(CODES[tab])}
                                                    </Typography>
                                                }
                                                component={Link}
                                                to={tab}
                                                value={tab}
                                            />
                                        )
                                )}
                            </Tabs>
                            <Box mt={8}>
                                {Object.entries(TABS).map(
                                    ([key, {featureFlag, Component}]) =>
                                        isLoaded &&
                                        tab === key &&
                                        getFnTab(featureFlag, <Component key={key} />)
                                )}
                            </Box>
                        </FortnigtContainer>
                    </LegendsLimitWarningProvider>
                </BulkCSVExportProvider>
            </TimeRangeLimitWarningProvider>
        </PDFExportProvider>
    );
};

export default withPageLayout(FortniteGamesView);
